import React from 'react';

import CardDialog from '~/components/CardDialog';
import Typography from '~/components/core/Atomic/Typography';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimsDisplayRet, ClaimsDisplayRetClaimDisplayEntry } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import { getClaimsTableColumnsByUser } from './ClaimsTable';
import useOrganization from './OrganizationContext';
import PlainTable from './PlainTable';

interface RelatedClaimsTabProps {
  claimId: string | number;
}

const RelatedClaimsTab: React.FC<RelatedClaimsTabProps> = ({ claimId }) => {
  const { user, userOrganization } = useCms();
  const organizationPreferences = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();
  const showRelatedClaimsTab = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.SHOW_RELATED_CLAIMS_TAB);

  const {
    isLoading: loading,
    isError,
    data: relatedClaimsData,
  } = useDataFetcher<ClaimsDisplayRet>(
    showRelatedClaimsTab ? `/api/v1/claims/${claimId}/related_claims_by_entity_id` : null
  );

  const relatedClaims = relatedClaimsData?.claims || [];
  const error = isError ? 'Failed to load related claims' : null;

  if (!showRelatedClaimsTab) return null;

  // Get column count for skeleton (7 columns after filtering out 2 columns + adding 1 policy column)
  const skeletonColumnCount = 7;

  // Loading state with skeleton
  if (loading || error) {
    return (
      <div className="m-12">
        <CardDialog title="Related Claims">
          <SkeletonTable rowsCount={5} columnsCount={skeletonColumnCount} isError={!!error} />
        </CardDialog>
      </div>
    );
  }

  // Empty state
  if (relatedClaims.length === 0) {
    return (
      <div className="m-12">
        <CardDialog title="Related Claims (0)">
          <div className="p-4 text-center">
            <Typography variant="body1" color="textSecondary">
              No related claims found for this entity
            </Typography>
          </div>
        </CardDialog>
      </div>
    );
  }

  // Get base column data and filter out unwanted columns
  let columnData = getClaimsTableColumnsByUser({
    user,
    organization: userOrganization,
    trackingInfo: 'relatedClaims',
    onClaimLinkClick: (claim: ClaimsDisplayRetClaimDisplayEntry) => window.open(`/claims/${claim.id}`, '_blank'), // Open in new tab
    organizationPreferences,
    lobConfigurationsDict,
    disableSortColumnsList: undefined,
  });

  // Filter out specified columns
  const filteredOutColumns = ['last_touched', 'insured_contact_full_name'];
  columnData = columnData.filter((column) => !filteredOutColumns.includes(column.id));

  // Add Policy Number column after the first column (Claim)
  // disableSort is a no-op here (PlainTable disables sorting globally) but needed for TS type compatibility
  const policyNumberColumn = {
    id: 'policy_number',
    numeric: false,
    disablePadding: false,
    disableSort: false as const,
    label: 'Policy Number',
    specialCell: (row: ClaimsDisplayRetClaimDisplayEntry) => (
      <span style={{ whiteSpace: 'nowrap' }}>{row.policy_number || 'N/A'}</span>
    ),
    specialCmpFunc: (row1: ClaimsDisplayRetClaimDisplayEntry, row2: ClaimsDisplayRetClaimDisplayEntry) => {
      const policy1 = row1.policy_number || '';
      const policy2 = row2.policy_number || '';
      return policy1.localeCompare(policy2);
    },
  };

  // Insert Policy Number column after the first column (index 1)
  columnData.splice(1, 0, policyNumberColumn);

  return (
    <div className="m-12">
      <CardDialog title={`Related Claims (${relatedClaims.length})`}>
        <PlainTable
          columns={columnData}
          rows={relatedClaims}
          maxHeight="500px" // Limit height for tab context
        />
      </CardDialog>
    </div>
  );
};

export default RelatedClaimsTab;
